import { Download, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { billingPdfFilename, createBillingPdf } from '../lib/billingPdf';
import { useCloudFile } from '../lib/useCloudFile';
import {
  invoicePaidAmount,
  quoteDiscountAmount,
  quoteSubtotal,
  quoteTotal,
  quoteWithholdingAmount,
  roundMoney,
  type CompanyProfile,
  type Invoice,
  type Payment,
  type PaymentMethod,
  type Plan,
} from '../types';
import { QuotePdfPages } from './QuotePdfPages';
import { DocumentParties } from './DocumentParties';

interface BillingDocumentPreviewProps {
  invoice: Invoice;
  payments: Payment[];
  company: CompanyProfile;
  plan: Plan;
  payment?: Payment;
  onClose: () => void;
}

const money = (value: number) => value.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const date = (value: string) => new Intl.DateTimeFormat('th-TH', { dateStyle: 'medium' }).format(new Date(value));
const paymentMethodLabels: Record<PaymentMethod, string> = {
  bank_transfer: 'โอนเงิน',
  cash: 'เงินสด',
  cheque: 'เช็ค',
  other: 'อื่น ๆ',
};

export function BillingDocumentPreview({ invoice, payments, company, plan, payment, onClose }: BillingDocumentPreviewProps) {
  const logo = useCloudFile(company.logoDataUrl);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [pdfError, setPdfError] = useState('');
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
  const [pdfPageCount, setPdfPageCount] = useState(0);
  const quote = invoice.quoteSnapshot;
  const isReceipt = Boolean(payment);
  const documentNumber = payment?.receiptNumber ?? invoice.number;
  const documentTitle = isReceipt ? 'ใบเสร็จรับเงิน' : 'ใบแจ้งหนี้';
  const documentEnglish = isReceipt ? 'RECEIPT' : 'INVOICE';
  const subtotal = quoteSubtotal(quote);
  const discountAmount = quoteDiscountAmount(quote);
  const withholdingAmount = quoteWithholdingAmount(quote);
  const taxable = Math.max(0, subtotal - discountAmount);
  const total = quoteTotal(quote);
  const activePayments = payments.filter((item) => item.invoiceId === invoice.id && !item.voidedAt);
  const paidTotal = invoicePaidAmount(invoice.id, payments);
  const paidThroughReceipt = payment
    ? roundMoney(activePayments.filter((item) => item.createdAt <= payment.createdAt).reduce((sum, item) => sum + item.amount, 0))
    : paidTotal;
  const receiptBalance = roundMoney(Math.max(0, total - paidThroughReceipt));
  const brandLabel = company.brandName?.trim() || company.name;
  const brandInitials = brandLabel.split(/\s+/).filter(Boolean).map((part) => part[0]).join('').slice(0, 2).toUpperCase() || 'BF';

  useEffect(() => {
    let cancelled = false;
    setGeneratingPdf(true);
    setPdfError('');
    const timer = window.setTimeout(() => {
      const documentElement = document.getElementById('billing-document');
      if (!documentElement) return;
      void createBillingPdf(documentElement, documentNumber).then(({ blob, pageCount }) => {
        if (cancelled) return;
        setPdfBlob(blob);
        setPdfPageCount(pageCount);
        setGeneratingPdf(false);
      }).catch((error) => {
        if (cancelled) return;
        setPdfError(error instanceof Error ? error.message : 'สร้าง PDF ไม่สำเร็จ กรุณาลองอีกครั้ง');
        setGeneratingPdf(false);
      });
    }, 180);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [company, documentNumber, invoice, payment, payments, plan]);

  const downloadPdf = () => {
    if (!pdfBlob || generatingPdf) return;
    const url = URL.createObjectURL(pdfBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = billingPdfFilename(documentNumber);
    link.click();
    window.setTimeout(() => URL.revokeObjectURL(url), 1_000);
  };

  return <div className="modal-backdrop preview-backdrop billing-preview-backdrop" role="presentation" onMouseDown={onClose}>
    <section className="modal preview-modal quote-preview-modal billing-pdf-preview-modal" role="dialog" aria-modal="true" onMouseDown={(event) => event.stopPropagation()}>
      <header className="modal-header no-print"><div className="preview-heading"><span className="eyebrow">ตัวอย่าง{documentTitle}จาก PDF จริง</span><h2>{documentNumber}</h2><small>{generatingPdf ? 'กำลังอัปเดตตัวอย่าง…' : pdfPageCount ? `${pdfPageCount} หน้า` : ''}</small></div><div className="header-actions">{pdfError && <span className="pdf-error" role="alert">{pdfError}</span>}<button className="btn primary preview-download" type="button" onClick={downloadPdf} disabled={generatingPdf || !pdfBlob}><Download size={17} /> {generatingPdf ? 'กำลังเตรียม…' : 'ดาวน์โหลด PDF'}</button><button className="icon-btn" type="button" onClick={onClose} aria-label="ปิด"><X size={20} /></button></div></header>
      <div className="quote-pdf-preview-body">
        {generatingPdf && !pdfBlob && <div className="mobile-pdf-message" role="status"><span className="spin" /> กำลังสร้างตัวอย่างจาก PDF จริง…</div>}
        {pdfError && !pdfBlob && <div className="mobile-pdf-message error" role="alert">{pdfError}</div>}
        {pdfBlob && <QuotePdfPages blob={pdfBlob} maxWidth={860} />}
      </div>
      <div className="quote-preview-pdf-source" aria-hidden="true"><div className="document billing-document pdf-export" id="billing-document">
        <div className="document-topline" />
        <header className="document-header"><div className="document-brand" data-cloud-loading={logo.loading || undefined} data-cloud-error={logo.error || undefined}>{logo.url ? <img src={logo.url} alt={`${brandLabel} logo`} /> : <span>{brandInitials}</span>}<div><strong>{brandLabel}</strong><small>{documentEnglish}</small></div></div><div className="document-title"><h1>{documentTitle}</h1><p>{documentEnglish}</p></div></header>
        <div className="document-meta"><div><span>เลขที่เอกสาร</span><strong>{documentNumber}</strong></div><div><span>{isReceipt ? 'วันที่รับเงิน' : 'วันที่ออก'}</span><strong>{date(payment?.paidAt ?? invoice.issueDate)}</strong></div><div><span>{isReceipt ? 'อ้างอิงใบแจ้งหนี้' : 'อ้างอิงใบเสนอราคา'}</span><strong>{isReceipt ? invoice.number : quote.number}</strong></div></div>
        <DocumentParties company={company} quote={quote} />
        <table className="document-table"><thead><tr><th>#</th><th>รายละเอียด</th><th>จำนวน</th><th>หน่วย</th><th>ราคา/หน่วย</th><th>รวม</th></tr></thead><tbody>{quote.items.map((item, index) => <tr key={item.id}><td>{index + 1}</td><td>{item.description}</td><td>{item.quantity}</td><td>{item.unit?.trim() || 'งาน'}</td><td>฿{money(item.unitPrice)}</td><td>฿{money(item.quantity * item.unitPrice)}</td></tr>)}</tbody></table>
        <div className="billing-final-block">
        <div className="document-summary"><div className="document-note">{isReceipt && payment ? <><span className="doc-label receipt-payment-title">รายละเอียดการรับชำระ</span><div className="receipt-payment-details"><p><span>ช่องทาง</span><strong>{paymentMethodLabels[payment.method]}</strong></p>{payment.reference && <p><span>เลขอ้างอิง</span><strong>{payment.reference}</strong></p>}<p><span>ชำระสำหรับใบแจ้งหนี้</span><strong>{invoice.number}</strong></p>{payment.note && <p><span>หมายเหตุ</span><strong>{payment.note}</strong></p>}</div></> : <><span className="doc-label">วันที่ครบกำหนดชำระ</span><p className="invoice-due-date">{date(invoice.dueDate)}</p>{company.bankAccount && <><span className="doc-label payment-label">ชำระเงิน</span><p>{company.bankName} · {company.bankAccount}</p></>}</>}</div><div className="document-totals"><div><span>ยอดรวม</span><strong>฿{money(subtotal)}</strong></div><div><span>ส่วนลด{quote.discountType === 'percent' ? ` ${quote.discount}%` : ''}</span><strong>−฿{money(discountAmount)}</strong></div><div><span>VAT {quote.taxRate}%</span><strong>฿{money(roundMoney(taxable * quote.taxRate / 100))}</strong></div>{quote.withholdingTaxRate > 0 && <div><span>หัก ณ ที่จ่าย {quote.withholdingTaxRate}%</span><strong>−฿{money(withholdingAmount)}</strong></div>}<div className="document-grand"><span>ยอดสุทธิ</span><strong>฿{money(total)}</strong></div></div></div>
        {isReceipt && payment && <section className="receipt-settlement"><div><span>รับชำระครั้งนี้</span><strong>฿{money(payment.amount)}</strong></div><div><span>รับชำระสะสม</span><strong>฿{money(paidThroughReceipt)}</strong></div><div><span>ยอดคงเหลือหลังรายการนี้</span><strong>฿{money(receiptBalance)}</strong></div></section>}
        {!isReceipt && <section className="invoice-payment-summary"><span>สถานะการรับชำระ ณ เวลาพิมพ์</span><strong>รับแล้ว ฿{money(paidTotal)} · คงเหลือ ฿{money(Math.max(0, total - paidTotal))}</strong></section>}
        <footer className="document-footer"><p>{isReceipt ? 'ได้รับเงินตามจำนวนข้างต้นแล้ว' : 'กรุณาชำระเงินภายในวันที่กำหนด'}</p>{plan === 'free' ? <span className="quote-credit">สร้างเอกสารด้วย <strong>BilaFlow</strong></span> : <span>ข้อมูลเอกสารนี้อยู่ภายใต้การควบคุมของผู้ใช้</span>}</footer>
        </div>
      </div></div>
    </section>
  </div>;
}
